/**
 * Compact horizontal Master → Multi-View → Orbit chain for the From-Designs overlay.
 * Second layout alongside HubFlowChain (sheet mode); does not replace it.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowLeft,
  Box,
  Heart,
  LayoutGrid,
  Layers,
  ImageOff,
  Loader2,
  Lock,
  MoreHorizontal,
  Orbit,
  Pencil,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { HubCollection, HubStyleCard } from "@/lib/fetchStyles";
import { fetchStyleStage } from "@/lib/fetchStyleStage";
import { DEFAULT_STUDIO_ORG_KEY } from "@/lib/shoeStudioPrefs";
import {
  ANGLE_KIND_ALIASES,
  ANGLE_KIND_ORDER,
  hasAllRequiredAngles,
} from "@/features/footwear-studio/studioHelpers";
import { SignedOutputImage } from "@/features/footwear-studio/SignedOutputImage";
import { colorwayDisplayLabel } from "@shared/curationLabels";
import { StyleActionsMenu } from "@/features/footwear-studio/StyleActionsMenu";
import type { RecentlyDeletedItem } from "@/lib/styleCurationApi";
import {
  alsoKindsShort,
  formatPurgeDate,
} from "@/features/footwear-studio/deleteUiHelpers";
import {
  flowOrbitMode,
  flowOrbitOpenContext,
  flowOrbitOpenLabel,
  flowOrbitStatusLine,
} from "@/features/footwear-studio/orbit/flowOrbitStatus";
import type {
  HubFlowVersion,
  HubOpenStage,
  HubOpenStageContext,
} from "@/features/footwear-studio/HubFlowChain";
import { SquareLoader } from "@/components/ui/square-loader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** CURATION-E1b-1 / DEL-B — style-level ⋯ actions in the style window header. */
export type OverlayStyleActions = {
  collections: HubCollection[];
  currentCollectionId: string | null;
  onMove: (collectionId: string) => void;
  onNewCollection?: () => void;
  onDelete: () => void;
};

export type OverlayOpenStageContext = HubOpenStageContext;

export type OverlayOpenStageHandler = (
  styleId: string,
  stage: HubOpenStage,
  context?: OverlayOpenStageContext,
) => void | boolean | Promise<void | boolean>;

type OverlayFlowChainProps = {
  styleId: string;
  styleCard?: HubStyleCard | null;
  versionHistory: HubFlowVersion[];
  versionsLoading: boolean;
  /** ALL version-history jobs for this style (not the display slice). */
  totalVersionCount?: number;
  activeMasterJobId: string | null;
  pinnedJobIds: string[];
  /** CURATION-E1 — colourways grouped under parent master job ids. */
  colourwaysByParent?: Record<string, HubFlowVersion[]>;
  onBack: () => void;
  onClose: () => void;
  showClose?: boolean;
  loadingDesign?: boolean;
  /** Terminal failed open (ceiling or hydrate reject) — ImageOff, not a silent clear. */
  designLoadFailed?: boolean;
  onSelectVersion?: (entry: HubFlowVersion) => void;
  onTogglePin?: (jobId: string) => void;
  onOpenStage?: OverlayOpenStageHandler;
  onMakeMaster?: (jobId: string) => Promise<void>;
  /** DEL-B — request soft-delete confirm for a job (version/colorway/MV/orbit). */
  onRequestDeleteJob?: (opts: {
    jobId: string;
    kind: string;
    label: string;
    isMaster?: boolean;
  }) => void;
  /** CURATION-E1b-3 — promote colorway → version; returns new/existing version job id. */
  onPromoteColorway?: (colorwayJobId: string) => Promise<string | null>;
  onRename?: (name: string) => Promise<void>;
  /** CURATION-E1b-1 / DEL-B — Home/Library style-level Move / Delete / Rename menu. */
  styleActions?: OverlayStyleActions | null;
  /** CURATION-F3b — open focused on a version or colorway job (once per id). */
  focusJobId?: string | null;
  focusKind?: "version" | "colorway" | null;
  /** DEL-B — recently deleted roots for this style (filtered by parent). */
  recentlyDeletedForStyle?: RecentlyDeletedItem[];
  onRestoreDeleted?: (item: RecentlyDeletedItem) => void;
  /** DEL-C — permanent purge from Recently deleted. */
  onPurgeForeverDeleted?: (item: RecentlyDeletedItem) => void;
};

const SECTION_LABEL_CLASS =
  "w-full text-left text-[10px] font-medium text-textMuted uppercase tracking-[0.14em] px-1";
/** Landscape 3:2 colourway thumbs (version cards are 240×160). */
const COLOURWAY_THUMB_W = 96;
const COLOURWAY_THUMB_H = 64;

type SelectedTab = "master" | "angles" | "orbit";

type AnglesStageState = {
  loading: boolean;
  hasJob: boolean;
  /** 2d_angles job id for the currently shown Multi-View set. */
  jobId: string | null;
  parentJobId: string | null;
  byKind: Record<string, string>;
};

type OrbitStageState = {
  loading: boolean;
  job: {
    id: string;
    status: string;
    result?: unknown;
  } | null;
};

function urlForKind(
  byKind: Record<string, string>,
  kind: string,
): string | undefined {
  if (byKind[kind]) return byKind[kind];
  const legacyEntry = Object.entries(ANGLE_KIND_ALIASES).find(
    ([, canonical]) => canonical === kind,
  );
  const legacyKey = legacyEntry?.[0];
  return legacyKey ? byKind[legacyKey] : undefined;
}

function formatRelativeTimestamp(date: Date): string {
  const ms = Date.now() - date.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function OpenStageButton({
  label,
  icon: Icon,
  onClick,
  disabled = false,
  loading = false,
}: {
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const showSpinner = loading;
  const effectiveDisabled = disabled || loading;
  return (
    <button
      type="button"
      disabled={effectiveDisabled}
      onClick={effectiveDisabled ? undefined : onClick}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center shrink-0",
        effectiveDisabled
          ? "cursor-not-allowed"
          : "cursor-pointer hover:bg-[rgba(56,189,248,0.12)] hover:border-[#38bdf8]",
      )}
      style={{
        gap: 7,
        fontSize: 12.5,
        fontWeight: 500,
        color: effectiveDisabled ? "var(--text-faint)" : "var(--text-primary)",
        background: "transparent",
        border: effectiveDisabled
          ? "1px solid rgba(255,255,255,0.10)"
          : "1px solid rgba(56,189,248,0.55)",
        borderRadius: 9999,
        padding: "9px 16px",
        transition: "background .15s, border-color .15s",
      }}
    >
      {showSpinner ? (
        <Loader2
          className="animate-spin shrink-0"
          style={{ width: 14, height: 14, color: "rgba(125,211,252,0.55)" }}
        />
      ) : (
        <Icon
          style={{
            width: 14,
            height: 14,
            color: effectiveDisabled ? "rgba(125,211,252,0.35)" : "#7dd3fc",
          }}
          strokeWidth={2}
        />
      )}
      {loading ? "Opening…" : label}
    </button>
  );
}

const ANGLE_STRIP_SKELETONS = 5;

/** Preview zone width when a right column shows — tune live. */
const PREVIEW_ZONE_W = "70%";
/** Version / angle column width — tune live. */
const VERSION_COLUMN_W = "30%";
/** Master version list card width (px) — tune live. */
const VERSION_LIST_CARD_W = 240;
const VERSION_LIST_CARD_IMG_H = 160;
/** Angle-rail label strip — must match skeleton `IMG_H + 28` and stay fixed loading→loaded. */
const VERSION_LIST_CARD_LABEL_H = 28;

/**
 * Selection chrome for angle rail cards.
 * Drawn on a z-20 overlay ABOVE hubThumbWhitePlate so the inset outline is never
 * covered mid-fade. Same colour / width / offset / ring as before; outer
 * overflow-hidden still clips the soft ring glow (loaded look unchanged).
 */
const ANGLE_RAIL_SELECTED_RING_CLASS =
  "pointer-events-none absolute inset-0 z-20 rounded-lg outline outline-2 outline-[#38bdf8] outline-offset-[-2px] ring-2 ring-[#38bdf8]/25";
/** Same #139 z-overlay ring for colourway thumbs (rounded-md). */
const COLOURWAY_SELECTED_RING_CLASS =
  "pointer-events-none absolute inset-0 z-20 rounded-md outline outline-2 outline-[#38bdf8] outline-offset-[-2px] ring-2 ring-[#38bdf8]/25";

const VERSION_COLUMN_CLASS =
  "shrink-0 h-full min-h-0 flex flex-col items-center justify-start";


function AngleRailCard({
  kind,
  label,
  url,
  isSelected,
  onSelect,
}: {
  kind: string;
  label: string;
  url: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const [plateOpaque, setPlateOpaque] = useState(false);

  useEffect(() => {
    setPlateOpaque(false);
  }, [url]);

  const cardH = VERSION_LIST_CARD_IMG_H + VERSION_LIST_CARD_LABEL_H;

  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-lg"
      style={{ width: VERSION_LIST_CARD_W, height: cardH }}
      data-testid={`angle-rail-card-${kind}`}
      data-angle-rail-loaded={plateOpaque ? "true" : "false"}
    >
      {isSelected ? (
        <div
          className={ANGLE_RAIL_SELECTED_RING_CLASS}
          aria-hidden
          data-testid="angle-rail-selection-ring"
        />
      ) : null}
      <button
        type="button"
        onClick={onSelect}
        className="relative block h-full w-full bg-bgNested text-left"
        aria-pressed={isSelected}
      >
        <SignedOutputImage
          storageRef={url}
          alt={label}
          className="absolute inset-0 h-full w-full"
          hubThumb
          hubThumbWhitePlate
          hubThumbWhitePlateExtendBottomPx={VERSION_LIST_CARD_LABEL_H}
          onHubOpaqueChange={setPlateOpaque}
        />
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 flex min-w-0 items-center gap-[5px] border-t px-2"
          style={{
            height: VERSION_LIST_CARD_LABEL_H,
            borderColor: plateOpaque ? "rgba(0,0,0,0.06)" : "transparent",
          }}
          data-testid="angle-rail-label-strip"
        >
          <span
            className="truncate text-[11px] font-medium"
            style={{
              color: plateOpaque ? "rgba(0,0,0,0.75)" : "rgba(255,255,255,0.55)",
            }}
            data-testid="angle-rail-label-text"
          >
            {label}
          </span>
        </div>
      </button>
    </div>
  );
}

function promotedFromCaption(
  entry: HubFlowVersion,
  versions: HubFlowVersion[],
): string | null {
  if (!entry.promotedFromColorwayJobId) return null;
  const name = entry.promotedFromColorwayName?.trim() || "colorway";
  const parent =
    entry.parentJobId != null
      ? versions.find((v) => v.jobId === entry.parentJobId)
      : null;
  if (parent) return `from V${parent.version} · ${name}`;
  return `from ${name}`;
}

function VersionCard({
  entry,
  isSelected,
  isMaster,
  isFavourite,
  colourwayCount,
  dimmed,
  showMenu,
  versions,
  makeMasterBusyJobId,
  onSelect,
  onTogglePin,
  onMakeMaster,
  onRequestDelete,
}: {
  entry: HubFlowVersion;
  isSelected: boolean;
  isMaster: boolean;
  isFavourite: boolean;
  colourwayCount: number;
  dimmed?: boolean;
  showMenu: boolean;
  versions: HubFlowVersion[];
  makeMasterBusyJobId?: string | null;
  onSelect: () => void;
  onTogglePin?: (jobId: string) => void;
  onMakeMaster?: (jobId: string) => Promise<void>;
  onRequestDelete?: (jobId: string) => void;
}) {
  const jobId = entry.jobId;
  const hidden = entry.isHidden === true;
  const fromCaption = !hidden ? promotedFromCaption(entry, versions) : null;
  const isBusy = !!jobId && jobId === makeMasterBusyJobId;
  const makeMasterBusy = !!makeMasterBusyJobId;

  return (
    <div
      className={cn(
        "group relative shrink-0 rounded-lg overflow-hidden bg-white transition-shadow",
        isSelected &&
          "outline outline-2 outline-[#38bdf8] outline-offset-[-2px] ring-2 ring-[#38bdf8]/25",
        dimmed && "opacity-50",
      )}
      style={{ width: VERSION_LIST_CARD_W }}
      data-testid={hidden ? "overlay-hidden-version-card" : "overlay-version-card"}
      data-make-master-busy={isBusy ? "true" : undefined}
      aria-busy={isBusy || undefined}
    >
      <button
        type="button"
        onClick={() => {
          if (isBusy) return;
          onSelect();
        }}
        className="block w-full text-left"
      >
        <div
          style={{ height: VERSION_LIST_CARD_IMG_H }}
          className="relative w-full bg-white"
        >
          <div className={cn("h-full w-full", isBusy && "opacity-60")}>
            <SignedOutputImage
              storageRef={entry.chipImageUrl ?? entry.imageUrl}
              alt={`V${entry.version}`}
              className="w-full h-full"
            />
          </div>
          {isBusy ? (
            <div
              className="pointer-events-none absolute inset-0 z-[15] flex items-center justify-center bg-bgNested/70"
              data-testid="overlay-version-make-master-busy"
            >
              <SquareLoader size={24} aria-hidden />
            </div>
          ) : null}
          {entry.hasAngles && (
            <span
              className="absolute top-2 left-2 flex items-center justify-center"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: "rgba(17,17,17,0.78)",
                pointerEvents: "none",
              }}
              title="Has Multi-View angles"
              aria-label="Has Multi-View angles"
            >
              <Box size={14} color="#fff" strokeWidth={1.5} aria-hidden />
            </span>
          )}
        </div>
        <div
          className="flex items-center gap-[5px] px-2 min-w-0 border-t border-black/[0.06] flex-wrap"
          style={{ height: VERSION_LIST_CARD_LABEL_H, minHeight: VERSION_LIST_CARD_LABEL_H }}
        >
          <span
            className="text-[11px] font-medium truncate"
            style={{ color: "rgba(0,0,0,0.75)" }}
          >
            V{entry.version}
          </span>
          {isMaster && !hidden ? (
            <span className="text-[9px] font-semibold tracking-[0.08em] text-[#34d399]">
              MASTER
            </span>
          ) : null}
          {isFavourite && !hidden ? (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold tracking-[0.08em] text-rose-300">
              <Heart className="h-2.5 w-2.5" fill="currentColor" />
              FAVOURITE
            </span>
          ) : null}
          {colourwayCount > 0 && !hidden ? (
            <span className="text-[10px] text-textMuted">
              {colourwayCount} colorway{colourwayCount === 1 ? "" : "s"}
            </span>
          ) : null}
          {fromCaption ? (
            <span
              className="text-[10px] text-[rgba(0,0,0,0.45)] ml-auto truncate"
              data-testid="overlay-version-promoted-from"
            >
              {fromCaption}
            </span>
          ) : null}
        </div>
      </button>
      {jobId && !isBusy && (onTogglePin || showMenu) ? (
        <div className="absolute top-2 right-2 flex items-center gap-0.5">
          {showMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    "p-1 rounded hover:bg-black/[0.06] transition-opacity",
                    "opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100",
                  )}
                  aria-label="Version actions"
                  data-testid="overlay-version-menu"
                >
                  <MoreHorizontal className="h-5 w-5 text-[#9ca3af]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[180px] rounded-[10px] border border-white/10 bg-bgSurface py-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
                onClick={(e) => e.stopPropagation()}
              >
                {onMakeMaster ? (
                  <DropdownMenuItem
                    disabled={isMaster || makeMasterBusy}
                    className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
                    data-testid="overlay-version-make-master"
                    onSelect={() => {
                      if (isMaster || makeMasterBusy) return;
                      void onMakeMaster(jobId).catch(() => {
                        /* Home/Library already toast "Couldn't save" */
                      });
                    }}
                  >
                    {isMaster ? "Master ✓" : "Make master"}
                  </DropdownMenuItem>
                ) : null}
                {onTogglePin ? (
                  <DropdownMenuItem
                    className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
                    onSelect={() => onTogglePin(jobId)}
                  >
                    {isFavourite ? "Remove from favourites" : "Favourite"}
                  </DropdownMenuItem>
                ) : null}
                {onRequestDelete ? (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem
                      className="cursor-pointer px-2.5 py-1.5 text-[12px] text-rose-300 focus:bg-rose-400/15 focus:text-rose-200"
                      data-testid="overlay-version-delete"
                      onSelect={() => onRequestDelete(jobId)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {onTogglePin && !hidden ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(jobId);
              }}
              className={cn(
                "p-1 rounded hover:bg-black/[0.06] transition-opacity",
                isFavourite
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100",
              )}
              title={
                isFavourite ? "Remove from favourites" : "Favourite"
              }
              aria-label={
                isFavourite ? "Remove from favourites" : "Favourite"
              }
            >
              <Heart
                className="h-5 w-5"
                fill={isFavourite ? "#fb7185" : "none"}
                stroke={isFavourite ? "#fb7185" : "#9ca3af"}
              />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ColourwayRow({
  parentVersion,
  colourways,
  selectedColourwayId,
  pinnedJobIds,
  versionHistory,
  totalVersionCount,
  makeMasterBusyJobId,
  onSelectColourway,
  onTogglePin,
  onRequestDelete,
  onPromoteColorway,
}: {
  parentVersion: number;
  colourways: HubFlowVersion[];
  selectedColourwayId: string | null;
  pinnedJobIds: string[];
  versionHistory: HubFlowVersion[];
  totalVersionCount: number;
  makeMasterBusyJobId?: string | null;
  onSelectColourway: (entry: HubFlowVersion) => void;
  onTogglePin?: (jobId: string) => void;
  onRequestDelete?: (jobId: string) => void;
  onPromoteColorway?: (colorwayJobId: string) => Promise<string | null>;
}) {
  const visible = colourways.filter((c) => c.isHidden !== true);
  const makeMasterBusy = !!makeMasterBusyJobId;

  if (visible.length === 0) return null;

  const rendered = visible;

  return (
    <div className="flex w-full flex-col gap-1.5" style={{ width: VERSION_LIST_CARD_W }}>
      <p className={SECTION_LABEL_CLASS}>
        Colorways from V{parentVersion}
      </p>
      <div className="flex flex-wrap gap-2">
        {rendered.map((cw, i) => {
          const selected = cw.id === selectedColourwayId;
          const label = colorwayDisplayLabel(
            cw.label != null ? { colorway_name: cw.label } : {},
            i + 1,
          );
          const jobId = cw.jobId;
          const isFavourite = !!jobId && pinnedJobIds.includes(jobId);
          const cwHidden = cw.isHidden === true;
          const showCwMenu = !!(onRequestDelete || onPromoteColorway);
          const isBusy = !!jobId && jobId === makeMasterBusyJobId;
          const alreadyPromoted = jobId
            ? versionHistory.find(
                (v) => v.promotedFromColorwayJobId === jobId,
              )
            : undefined;
          const promoteSublabel = alreadyPromoted
            ? `is V${alreadyPromoted.version}`
            : `becomes V${totalVersionCount + 1}`;
          return (
            <button
              key={cw.id}
              type="button"
              onClick={() => {
                if (isBusy) return;
                onSelectColourway(cw);
              }}
              className={cn(
                "shrink-0 flex flex-col text-left",
                cwHidden && "opacity-50",
              )}
              style={{ width: COLOURWAY_THUMB_W }}
              data-testid="overlay-colourway-thumb"
              data-hidden={cwHidden ? "true" : undefined}
              data-make-master-busy={isBusy ? "true" : undefined}
              aria-busy={isBusy || undefined}
            >
              {/*
                #138/#139 loading: slot bg-bgNested; white only via hubThumbWhitePlate
                fading with the image; SquareLoader 16 centred; selection ring z-20
                overlay so it is not clipped while the plate fades in.
              */}
              <div
                className="group relative shrink-0 overflow-hidden rounded-md"
                style={{ width: COLOURWAY_THUMB_W, height: COLOURWAY_THUMB_H }}
              >
                {selected ? (
                  <div
                    className={COLOURWAY_SELECTED_RING_CLASS}
                    aria-hidden
                    data-testid="overlay-colourway-selection-ring"
                  />
                ) : null}
                <div
                  className={cn(
                    "relative h-full w-full bg-bgNested",
                    isBusy && "opacity-60",
                  )}
                >
                  <SignedOutputImage
                    storageRef={cw.chipImageUrl ?? cw.imageUrl}
                    alt={label}
                    className="h-full w-full"
                    hubThumb
                    hubThumbWhitePlate
                    hubThumbLoaderSize={16}
                  />
                </div>
                {isBusy ? (
                  <div
                    className="pointer-events-none absolute inset-0 z-[15] flex items-center justify-center bg-bgNested/70"
                    data-testid="overlay-colourway-make-master-busy"
                  >
                    <SquareLoader size={16} aria-hidden />
                  </div>
                ) : null}
                {jobId && !isBusy && (onTogglePin || showCwMenu) ? (
                  <div className="absolute top-1 right-1 z-30 flex gap-0.5">
                    {showCwMenu ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            className={cn(
                              "p-0.5 rounded-[4px] bg-white/85 text-[#6b7280] hover:text-[#111] transition-opacity",
                              "opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100",
                            )}
                            aria-label="Colorway actions"
                            data-testid="overlay-colourway-menu"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="min-w-[180px] rounded-[10px] border border-white/10 bg-bgSurface py-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {onPromoteColorway ? (
                            <DropdownMenuItem
                              disabled={makeMasterBusy}
                              className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
                              data-testid="overlay-colourway-make-master"
                              onSelect={() => {
                                if (makeMasterBusy) return;
                                void onPromoteColorway(jobId);
                              }}
                            >
                              <span className="flex w-full items-center justify-between gap-3">
                                <span>Make master</span>
                                <span className="text-[10px] text-white/45 shrink-0">
                                  {promoteSublabel}
                                </span>
                              </span>
                            </DropdownMenuItem>
                          ) : null}
                          {onTogglePin ? (
                            <DropdownMenuItem
                              className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
                              onSelect={() => onTogglePin(jobId)}
                            >
                              {isFavourite
                                ? "Remove from favourites"
                                : "Favourite"}
                            </DropdownMenuItem>
                          ) : null}
                          {onRequestDelete ? (
                            <>
                              <DropdownMenuSeparator className="bg-white/10" />
                              <DropdownMenuItem
                                className="cursor-pointer px-2.5 py-1.5 text-[12px] text-rose-300 focus:bg-rose-400/15 focus:text-rose-200"
                                data-testid="overlay-colourway-delete"
                                onSelect={() => onRequestDelete(jobId)}
                              >
                                Delete
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                    {onTogglePin && !cwHidden ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTogglePin(jobId);
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        className={cn(
                          "p-0.5 rounded-[4px] bg-white/85 text-[#6b7280] hover:text-[#111] transition-opacity",
                          isFavourite
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100",
                        )}
                        title={
                          isFavourite ? "Remove from favourites" : "Favourite"
                        }
                        aria-label={
                          isFavourite ? "Remove from favourites" : "Favourite"
                        }
                        data-testid="overlay-colourway-fav"
                      >
                        <Heart
                          className="h-3.5 w-3.5"
                          fill={isFavourite ? "#fb7185" : "none"}
                          stroke={isFavourite ? "#fb7185" : "currentColor"}
                        />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <span
                className="mt-1 truncate text-[10px] text-textSecondary"
                style={{ maxWidth: COLOURWAY_THUMB_W }}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MasterVersionList({
  versions,
  selectedId,
  selectedColourwayId,
  activeMasterJobId,
  pinnedJobIds,
  colourwaysByParent,
  totalVersionCount,
  makeMasterBusyJobId,
  onSelect,
  onSelectColourway,
  onTogglePin,
  onMakeMaster,
  onRequestDelete,
  onPromoteColorway,
  recentlyDeletedForStyle,
  onRestoreDeleted,
  onPurgeForeverDeleted,
}: {
  versions: HubFlowVersion[];
  selectedId: string | null;
  selectedColourwayId: string | null;
  activeMasterJobId: string | null;
  pinnedJobIds: string[];
  colourwaysByParent?: Record<string, HubFlowVersion[]>;
  totalVersionCount: number;
  makeMasterBusyJobId?: string | null;
  onSelect: (id: string) => void;
  onSelectColourway: (entry: HubFlowVersion) => void;
  onTogglePin?: (jobId: string) => void;
  onMakeMaster?: (jobId: string) => Promise<void>;
  onRequestDelete?: (opts: {
    jobId: string;
    kind: string;
    label: string;
    isMaster?: boolean;
  }) => void;
  onPromoteColorway?: (colorwayJobId: string) => Promise<string | null>;
  recentlyDeletedForStyle?: RecentlyDeletedItem[];
  onRestoreDeleted?: (item: RecentlyDeletedItem) => void;
  onPurgeForeverDeleted?: (item: RecentlyDeletedItem) => void;
}) {
  const showMenu = !!(onMakeMaster || onRequestDelete);

  const visible = versions.filter((v) => v.isHidden !== true);

  const master = visible
    .filter((v) => v.jobId && v.jobId === activeMasterJobId)
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const favourites = visible
    .filter(
      (v) =>
        v.jobId &&
        pinnedJobIds.includes(v.jobId) &&
        v.jobId !== activeMasterJobId,
    )
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const masterIds = new Set(master.map((v) => v.jobId));
  const favIds = new Set(favourites.map((v) => v.jobId));
  const others = visible
    .filter((v) => v.jobId && !masterIds.has(v.jobId) && !favIds.has(v.jobId))
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const renderSection = (label: string, list: HubFlowVersion[]) => {
    if (list.length === 0) return null;
    return (
      <div className="flex w-full flex-col items-center gap-3">
        <p className={SECTION_LABEL_CLASS} style={{ width: VERSION_LIST_CARD_W }}>
          {label}
        </p>
        {list.map((entry) => {
          const isMaster =
            !!entry.jobId && entry.jobId === activeMasterJobId;
          const isFavourite =
            !!entry.jobId && pinnedJobIds.includes(entry.jobId);
          const cws =
            (entry.jobId && colourwaysByParent?.[entry.jobId]) || [];
          const visibleCwCount = cws.filter((c) => c.isHidden !== true).length;
          return (
            <div key={entry.id} className="flex flex-col items-center gap-2">
              <VersionCard
                entry={entry}
                isSelected={
                  entry.id === selectedId && !selectedColourwayId
                }
                isMaster={isMaster}
                isFavourite={isFavourite}
                colourwayCount={visibleCwCount}
                showMenu={showMenu}
                versions={versions}
                makeMasterBusyJobId={makeMasterBusyJobId}
                onSelect={() => onSelect(entry.id)}
                onTogglePin={onTogglePin}
                onMakeMaster={onMakeMaster}
                onRequestDelete={
                  entry.jobId && onRequestDelete
                    ? (jobId) =>
                        onRequestDelete({
                          jobId,
                          kind: "version",
                          label: `V${entry.version}`,
                          isMaster,
                        })
                    : undefined
                }
              />
              {colourwaysByParent ? (
                <ColourwayRow
                  parentVersion={entry.version}
                  colourways={cws}
                  selectedColourwayId={selectedColourwayId}
                  pinnedJobIds={pinnedJobIds}
                  versionHistory={versions}
                  totalVersionCount={totalVersionCount}
                  makeMasterBusyJobId={makeMasterBusyJobId}
                  onSelectColourway={onSelectColourway}
                  onTogglePin={onTogglePin}
                  onRequestDelete={
                    onRequestDelete
                      ? (jobId) => {
                          const cw = cws.find((c) => c.jobId === jobId);
                          onRequestDelete({
                            jobId,
                            kind: "colorway",
                            label:
                              cw?.label?.trim() ||
                              colorwayDisplayLabel({}, 1),
                          });
                        }
                      : undefined
                  }
                  onPromoteColorway={onPromoteColorway}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const deleted = recentlyDeletedForStyle ?? [];

  return (
    <div className="flex flex-col items-center w-full h-full min-h-0 overflow-y-auto stronom-scroll gap-4 py-1">
      {renderSection("Master", master)}
      {renderSection("Favourites", favourites)}
      {renderSection("Other versions", others)}
      {deleted.length > 0 ? (
        <div
          className="mt-2 w-full rounded-[8px] border border-dashed border-white/15 px-2.5 py-2"
          style={{ width: VERSION_LIST_CARD_W }}
          data-testid="overlay-recently-deleted"
        >
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-textMuted">
            Recently deleted · {deleted.length}
          </p>
          <ul className="flex flex-col gap-2">
            {deleted.map((item) => {
              const key =
                item.kind === "style"
                  ? `style:${item.style_id}`
                  : `job:${item.job_id}`;
              const label =
                typeof item.label === "string" && item.label.trim()
                  ? item.label.trim()
                  : item.kind === "style"
                    ? String(item.style_name ?? "Style")
                    : String(item.kind);
              const also = alsoKindsShort(item);
              const purge =
                typeof item.purge_at === "string"
                  ? formatPurgeDate(item.purge_at)
                  : "";
              return (
                <li
                  key={key}
                  className="flex items-start justify-between gap-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[11px] text-textPrimary">
                      {label}
                    </p>
                    <p className="truncate text-[10px] text-textMuted">
                      {also ? `${also} · ` : ""}
                      {purge ? `deletes for good on ${purge}` : null}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {onRestoreDeleted ? (
                      <button
                        type="button"
                        className="rounded-[6px] border border-white/10 px-1.5 py-0.5 text-[10px] text-textSecondary hover:text-textPrimary"
                        data-testid="overlay-restore-deleted"
                        onClick={() => onRestoreDeleted(item)}
                      >
                        Restore
                      </button>
                    ) : null}
                    {onPurgeForeverDeleted ? (
                      <button
                        type="button"
                        className="rounded-[6px] border border-white/10 px-1.5 py-0.5 text-[10px] text-rose-300 hover:text-rose-200"
                        data-testid="overlay-purge-forever"
                        onClick={() => onPurgeForeverDeleted(item)}
                      >
                        Delete forever
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function MasterStackSkeleton() {
  return (
    <div className="flex flex-col items-center w-full h-full min-h-0 gap-3 py-1">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={`sk-${i}`}
          className="shrink-0 rounded-lg animate-pulse"
          style={{
            width: VERSION_LIST_CARD_W,
            height: VERSION_LIST_CARD_IMG_H + 28,
            background: "rgba(255,255,255,0.08)",
          }}
        />
      ))}
    </div>
  );
}

export function OverlayFlowChain({
  styleId,
  styleCard = null,
  versionHistory,
  versionsLoading,
  totalVersionCount,
  activeMasterJobId,
  pinnedJobIds,
  colourwaysByParent,
  onBack,
  onClose,
  showClose = false,
  loadingDesign = false,
  designLoadFailed = false,
  onSelectVersion,
  onTogglePin,
  onOpenStage,
  onMakeMaster,
  onRequestDeleteJob,
  onPromoteColorway,
  onRename,
  styleActions = null,
  focusJobId = null,
  focusKind = null,
  recentlyDeletedForStyle = [],
  onRestoreDeleted,
  onPurgeForeverDeleted,
}: OverlayFlowChainProps) {
  const effectiveTotalVersionCount =
    typeof totalVersionCount === "number" && Number.isFinite(totalVersionCount)
      ? totalVersionCount
      : versionHistory.length;
  const [selectedTab, setSelectedTab] = useState<SelectedTab>("master");
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [selectedColourwayId, setSelectedColourwayId] = useState<string | null>(
    null,
  );
  const [selectedAngleKind, setSelectedAngleKind] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [localName, setLocalName] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const didAutoSelectTabRef = useRef(false);
  /** CURATION-F3b — apply focusJobId once after history loads. */
  const focusAppliedRef = useRef<string | null>(null);
  const [openingStage, setOpeningStage] = useState(false);
  const [anglesStage, setAnglesStage] = useState<AnglesStageState>({
    loading: true,
    hasJob: false,
    jobId: null,
    parentJobId: null,
    byKind: {},
  });
  const [orbitStage, setOrbitStage] = useState<OrbitStageState>({
    loading: true,
    job: null,
  });
  /** CURATION-E1b-3 busy — colorway promote or version make_master in flight. */
  const [makeMasterBusyJobId, setMakeMasterBusyJobId] = useState<string | null>(
    null,
  );
  const makeMasterBusyJobIdRef = useRef<string | null>(null);

  const sortedVersions = useMemo(
    () => [...versionHistory].sort((a, b) => b.version - a.version),
    [versionHistory],
  );

  const activeVersion = useMemo(() => {
    if (!activeMasterJobId) return null;
    return (
      versionHistory.find((v) => v.jobId === activeMasterJobId) ?? null
    );
  }, [versionHistory, activeMasterJobId]);

  const selectedMasterEntry = useMemo(() => {
    if (selectedMasterId) {
      const found = sortedVersions.find((v) => v.id === selectedMasterId);
      if (found) return found;
    }
    return sortedVersions.find((v) => v.isHidden !== true) ?? sortedVersions[0] ?? null;
  }, [sortedVersions, selectedMasterId]);

  const selectedColourwayEntry = useMemo(() => {
    if (!selectedColourwayId || !colourwaysByParent) return null;
    for (const list of Object.values(colourwaysByParent)) {
      const found = list.find((c) => c.id === selectedColourwayId);
      if (found) return found;
    }
    return null;
  }, [colourwaysByParent, selectedColourwayId]);

  // CURATION-E1b-2 — clear selection when selected colorway is hidden or gone.
  useEffect(() => {
    if (!selectedColourwayId) return;
    if (!colourwaysByParent) {
      setSelectedColourwayId(null);
      return;
    }
    let found: HubFlowVersion | undefined;
    for (const list of Object.values(colourwaysByParent)) {
      found = list.find((c) => c.id === selectedColourwayId);
      if (found) break;
    }
    if (!found || found.isHidden === true) {
      setSelectedColourwayId(null);
    }
  }, [selectedColourwayId, colourwaysByParent]);

  // DEL-B — after Multi-View / Orbit delete, return to Master tab.
  const hadAnglesJobRef = useRef(false);
  const hadOrbitJobRef = useRef(false);
  useEffect(() => {
    if (anglesStage.hasJob) hadAnglesJobRef.current = true;
    if (
      selectedTab === "angles" &&
      !anglesStage.loading &&
      !anglesStage.hasJob &&
      hadAnglesJobRef.current
    ) {
      hadAnglesJobRef.current = false;
      setSelectedTab("master");
    }
  }, [selectedTab, anglesStage.loading, anglesStage.hasJob]);
  useEffect(() => {
    if (orbitStage.job) hadOrbitJobRef.current = true;
    if (
      selectedTab === "orbit" &&
      !orbitStage.loading &&
      !orbitStage.job &&
      hadOrbitJobRef.current
    ) {
      hadOrbitJobRef.current = false;
      setSelectedTab("master");
    }
  }, [selectedTab, orbitStage.loading, orbitStage.job]);

  const openTargetEntry = useMemo(() => {
    if (selectedColourwayEntry?.jobId) {
      const parentId = Object.entries(colourwaysByParent ?? {}).find(([, list]) =>
        list.some((c) => c.id === selectedColourwayEntry.id),
      )?.[0];
      if (parentId) {
        return (
          sortedVersions.find((v) => v.jobId === parentId) ?? selectedMasterEntry
        );
      }
    }
    return selectedMasterEntry;
  }, [
    colourwaysByParent,
    selectedColourwayEntry,
    selectedMasterEntry,
    sortedVersions,
  ]);

  /** MV-SCOPE: Multi-View tab follows the selected version card. */
  const anglesParentJobId =
    selectedMasterEntry?.jobId ?? activeMasterJobId ?? null;

  const prevStyleIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const styleChanged = prevStyleIdRef.current !== styleId;
    prevStyleIdRef.current = styleId;
    if (styleChanged) {
      didAutoSelectTabRef.current = false;
      setSelectedTab("master");
      setSelectedColourwayId(null);
      setOrbitStage({ loading: true, job: null });
    }
    setAnglesStage({
      loading: true,
      hasJob: false,
      jobId: null,
      parentJobId: null,
      byKind: {},
    });
    void (async () => {
      const [anglesData, orbitData] = await Promise.all([
        fetchStyleStage(
          styleId,
          "angles",
          DEFAULT_STUDIO_ORG_KEY,
          anglesParentJobId,
        ),
        fetchStyleStage(styleId, "orbit", DEFAULT_STUDIO_ORG_KEY),
      ]);
      if (cancelled) return;

      const byKind: Record<string, string> = {};
      for (const out of anglesData?.outputs ?? []) {
        if (
          typeof out.kind === "string" &&
          out.kind.startsWith("image:angle-") &&
          typeof out.url === "string" &&
          out.url.trim()
        ) {
          byKind[out.kind] = out.url.trim();
        }
      }
      const anglesJobId =
        typeof anglesData?.job?.id === "string" ? anglesData.job.id : null;
      const hasAnglesJob = !!anglesJobId;
      setAnglesStage({
        loading: false,
        hasJob: hasAnglesJob,
        jobId: anglesJobId,
        parentJobId: anglesData?.parent_job_id ?? null,
        byKind,
      });
      const present = Object.keys(byKind).length;
      console.log(
        `[HUB3b2] multiangle style=${styleId} has_job=${hasAnglesJob} views=${present} parent=${anglesData?.parent_job_id ?? "null"} scoped=${anglesParentJobId ?? "null"}`,
      );

      const rawJob = (orbitData as { job?: Record<string, unknown> | null } | null)
        ?.job;
      const orbitJob =
        rawJob && typeof rawJob.id === "string" && typeof rawJob.status === "string"
          ? {
              id: rawJob.id,
              status: rawJob.status,
              result: rawJob.result,
            }
          : null;
      setOrbitStage({ loading: false, job: orbitJob });
      console.log(
        `[HUB3b2] orbit style=${styleId} has_job=${!!orbitJob} status=${orbitJob?.status ?? "null"}`,
      );

      if (!didAutoSelectTabRef.current) {
        didAutoSelectTabRef.current = true;
        const multiangleComplete = hasAllRequiredAngles(byKind);
        const orbitDone = orbitJob?.status === "done";
        if (orbitDone && multiangleComplete) {
          setSelectedTab("orbit");
        } else if (orbitDone) {
          setSelectedTab("orbit");
        } else if (present > 0) {
          setSelectedTab("angles");
        } else {
          setSelectedTab("master");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [styleId, anglesParentJobId]);

  const fromVersion = useMemo(() => {
    if (!anglesStage.parentJobId) return null;
    return (
      versionHistory.find((v) => v.jobId === anglesStage.parentJobId) ?? null
    );
  }, [versionHistory, anglesStage.parentJobId]);

  const angleSlots = useMemo(() => {
    return ANGLE_KIND_ORDER.map(({ kind, label }) => ({
      kind,
      label,
      url: urlForKind(anglesStage.byKind, kind),
    }));
  }, [anglesStage.byKind]);

  const presentCount = angleSlots.filter((s) => !!s.url).length;
  const multiangleComplete = hasAllRequiredAngles(anglesStage.byKind);
  const multiViewLocked = !anglesStage.loading && presentCount === 0;
  // Same lock rule as the old 3D tab: need a complete Multi-View.
  const orbitLocked = !anglesStage.loading && !multiangleComplete;
  const orbitMode = flowOrbitMode(orbitStage.job);

  const styleLabel = useMemo(() => {
    if (localName != null && localName.trim()) return localName.trim();
    if (!styleCard) return "Style";
    return (
      styleCard.name?.trim() ||
      (styleCard.style_number != null
        ? `Style ${styleCard.style_number}`
        : "Style")
    );
  }, [localName, styleCard]);

  useEffect(() => {
    setLocalName(null);
    setRenaming(false);
    setSavedFlash(false);
  }, [styleId]);

  const styleMeta = useMemo(() => {
    if (!styleCard) return null;
    const numberPart =
      styleCard.style_number != null
        ? `#${styleCard.style_number}`
        : "—";
    const typePart = styleCard.shoe_type?.trim() || "—";
    const relativePart = styleCard.last_activity_at
      ? formatRelativeTimestamp(new Date(styleCard.last_activity_at))
      : "—";
    return `${numberPart} · ${typePart} · ${relativePart}`;
  }, [styleCard]);

  const selectedAngleSlot = useMemo(() => {
    if (selectedAngleKind) {
      const found = angleSlots.find((s) => s.kind === selectedAngleKind);
      if (found) return found;
    }
    return angleSlots.find((s) => !!s.url) ?? angleSlots[0] ?? null;
  }, [angleSlots, selectedAngleKind]);

  // Reset focus bookkeeping when style changes or focus clears.
  useEffect(() => {
    focusAppliedRef.current = null;
  }, [styleId]);

  useEffect(() => {
    if (!focusJobId) focusAppliedRef.current = null;
  }, [focusJobId]);

  // CURATION-F3b — apply focus before auto-select wins (once per focusJobId).
  useEffect(() => {
    if (!focusJobId) return;
    if (versionsLoading) return;
    if (focusAppliedRef.current === focusJobId) return;

    const asColorway =
      focusKind === "colorway" ||
      (focusKind !== "version" &&
        !!colourwaysByParent &&
        Object.values(colourwaysByParent).some((list) =>
          list.some((c) => c.id === focusJobId || c.jobId === focusJobId),
        ));

    if (asColorway) {
      let parentId: string | null = null;
      for (const [pid, list] of Object.entries(colourwaysByParent ?? {})) {
        if (list.some((c) => c.id === focusJobId || c.jobId === focusJobId)) {
          parentId = pid;
          break;
        }
      }
      const parent = parentId
        ? sortedVersions.find((v) => v.id === parentId || v.jobId === parentId)
        : null;
      if (parent) {
        setSelectedMasterId(parent.id);
        setSelectedColourwayId(focusJobId);
        focusAppliedRef.current = focusJobId;
        return;
      }
      // Hidden / out of list — fall back to normal auto-select.
      focusAppliedRef.current = focusJobId;
      return;
    }

    const version = sortedVersions.find(
      (v) => v.id === focusJobId || v.jobId === focusJobId,
    );
    if (version) {
      setSelectedMasterId(version.id);
      setSelectedColourwayId(null);
      focusAppliedRef.current = focusJobId;
      return;
    }
    focusAppliedRef.current = focusJobId;
  }, [
    focusJobId,
    focusKind,
    versionsLoading,
    sortedVersions,
    colourwaysByParent,
  ]);

  useEffect(() => {
    if (selectedMasterId) return;
    // Wait until focus attempt finishes so auto-select does not race.
    if (focusJobId && focusAppliedRef.current !== focusJobId) return;
    if (activeVersion) {
      setSelectedMasterId(activeVersion.id);
    } else if (sortedVersions[0]) {
      setSelectedMasterId(sortedVersions[0].id);
    }
  }, [sortedVersions, activeVersion, selectedMasterId, focusJobId]);

  useEffect(() => {
    if (!selectedAngleKind) {
      const firstPresent = angleSlots.find((s) => !!s.url);
      if (firstPresent) {
        setSelectedAngleKind(firstPresent.kind);
      } else if (angleSlots[0]) {
        setSelectedAngleKind(angleSlots[0].kind);
      }
      return;
    }
    const stillPresent = angleSlots.find(
      (s) => s.kind === selectedAngleKind && !!s.url,
    );
    if (!stillPresent) {
      const firstPresent = angleSlots.find((s) => !!s.url);
      if (firstPresent) {
        setSelectedAngleKind(firstPresent.kind);
      }
    }
  }, [angleSlots, selectedAngleKind]);

  const openTargetIsMaster =
    !!openTargetEntry?.jobId &&
    openTargetEntry.jobId === activeMasterJobId;
  const openTargetHidden = openTargetEntry?.isHidden === true;

  const openLabel =
    selectedTab === "master"
      ? openTargetIsMaster
        ? "Open Master"
        : "Make master & open"
      : selectedTab === "angles"
        ? "Open Multi-View"
        : flowOrbitOpenLabel(orbitMode);

  const openDisabled =
    openingStage ||
    (selectedTab === "orbit" && orbitLocked) ||
    (selectedTab === "angles" && multiViewLocked) ||
    (selectedTab === "master" && openTargetHidden);

  const handleOpen = () => {
    if (openDisabled || !onOpenStage) return;
    const context: OverlayOpenStageContext =
      selectedTab === "master"
        ? { masterEntry: openTargetEntry }
        : selectedTab === "angles"
          ? { angleKind: selectedAngleSlot?.kind ?? null }
          : flowOrbitOpenContext(orbitMode);
    void (async () => {
      setOpeningStage(true);
      try {
        await onOpenStage(
          styleId,
          selectedTab === "orbit" ? "showcase" : selectedTab,
          context,
        );
      } finally {
        setOpeningStage(false);
      }
    })();
  };

  const selectVersionCard = (id: string) => {
    setSelectedMasterId(id);
    setSelectedColourwayId(null);
    const entry = sortedVersions.find((v) => v.id === id);
    if (entry) onSelectVersion?.(entry);
  };

  const selectColourway = (entry: HubFlowVersion) => {
    setSelectedColourwayId(entry.id);
    // Keep parent version selected for MV scope / open target resolution.
    const parentId = Object.entries(colourwaysByParent ?? {}).find(([, list]) =>
      list.some((c) => c.id === entry.id),
    )?.[0];
    if (parentId) {
      const parent = sortedVersions.find((v) => v.jobId === parentId);
      if (parent) setSelectedMasterId(parent.id);
    }
  };

  const showMasterStack =
    selectedTab === "master" &&
    (versionsLoading || sortedVersions.length > 0);
  const showAngleColumn = selectedTab === "angles";
  const showRightColumn = showMasterStack || showAngleColumn;

  const masterTabCount = versionsLoading ? "—" : String(versionHistory.length);
  const anglesTabCount = anglesStage.loading
    ? "—"
    : `${presentCount} of 5`;
  const orbitTabCount = orbitStage.loading
    ? "—"
    : orbitMode === "done"
      ? "ready"
      : "—";

  const tabs: {
    id: SelectedTab;
    label: string;
    icon: LucideIcon;
    count: string;
    locked?: boolean;
  }[] = [
    {
      id: "master",
      label: "Master",
      icon: LayoutGrid,
      count: masterTabCount,
    },
    {
      id: "angles",
      label: "Multi-View",
      icon: Layers,
      count: anglesTabCount,
      locked: multiViewLocked,
    },
    {
      id: "orbit",
      label: "Orbit",
      icon: Orbit,
      count: orbitTabCount,
      locked: orbitLocked,
    },
  ];

  const footerContext = (() => {
    if (selectedTab === "master") {
      if (versionsLoading) {
        return "Loading versions…";
      }
      if (selectedColourwayEntry) {
        const parent = openTargetEntry ?? selectedMasterEntry;
        const cwLabel = selectedColourwayEntry.label ?? "Colorway";
        return (
          <>
            <span className="font-semibold text-textSecondary">
              V{parent?.version ?? "?"}
            </span>
            <span> · Colorway {cwLabel}</span>
          </>
        );
      }
      const entry = selectedMasterEntry ?? activeVersion;
      if (!entry) return "No versions yet";
      if (entry.isHidden) {
        return (
          <>
            <span className="font-semibold text-textSecondary">V{entry.version}</span>
            <span> · Deleted — restore it from Recently deleted to use it</span>
          </>
        );
      }
      return (
        <>
          <span className="font-semibold text-textSecondary">V{entry.version}</span>
          <span> · {entry.intentText || "Initial generation"}</span>
          <span>
            {" "}
            · {entry.lockedCount}L{entry.allowedCount}A{entry.forbiddenCount}F
          </span>
        </>
      );
    }
    if (selectedTab === "angles") {
      if (anglesStage.loading) {
        return "Loading views…";
      }
      const angleLabel = selectedAngleSlot?.label ?? "view";
      const parentLabel = fromVersion
        ? `V${fromVersion.version}`
        : activeVersion
          ? `V${activeVersion.version}`
          : "master";
      return (
        <>
          Showing <span className="font-semibold text-textSecondary">{angleLabel}</span>
          <span> · all views derived from {parentLabel}</span>
        </>
      );
    }
    if (orbitLocked) {
      return `Requires all 5 angles · you have ${presentCount} of 5`;
    }
    if (orbitStage.loading) {
      return "Loading Orbit…";
    }
    return flowOrbitStatusLine(orbitStage.job);
  })();

  const heroEntry =
    selectedTab === "master"
      ? selectedColourwayEntry ?? selectedMasterEntry
      : null;

  const renderHero = () => {
    if (selectedTab === "master") {
      if (versionsLoading) {
        return (
          <div
            className="w-full h-full animate-pulse rounded-[11px]"
            style={{ background: "rgba(255,255,255,0.12)" }}
          />
        );
      }
      if (!heroEntry) {
        return (
          <div className="flex flex-col items-center justify-center gap-2 px-6 text-center">
            <LayoutGrid className="h-8 w-8" style={{ color: "#6b7080" }} />
            <p className="text-[14px]" style={{ color: "#6b7080" }}>
              No versions yet
            </p>
          </div>
        );
      }
      return (
        <SignedOutputImage
          storageRef={heroEntry.imageUrl}
          alt={
            selectedColourwayEntry
              ? selectedColourwayEntry.label ?? "Colorway"
              : `V${heroEntry.version}`
          }
          className="max-w-full max-h-full w-full h-full"
          hubThumb
          hubThumbLoaderSize={40}
          hubThumbWhitePlate
        />
      );
    }

    if (selectedTab === "angles") {
      if (anglesStage.loading) {
        return (
          <div
            className="w-full h-full animate-pulse rounded-[11px]"
            style={{ background: "rgba(255,255,255,0.12)" }}
          />
        );
      }
      if (selectedAngleSlot?.url) {
        return (
          <SignedOutputImage
            storageRef={selectedAngleSlot.url}
            alt={selectedAngleSlot.label}
            className="max-w-full max-h-full w-full h-full"
            hubThumb
            hubThumbLoaderSize={40}
            hubThumbWhitePlate
          />
        );
      }
      return (
        <div className="flex flex-col items-center justify-center gap-2 px-6 text-center">
          <Layers className="h-8 w-8" style={{ color: "#6b7080" }} />
          <p className="text-[14px]" style={{ color: "#6b7080" }}>
            {selectedAngleSlot?.label ?? "View"} not generated
          </p>
        </div>
      );
    }

    if (orbitLocked) {
      return (
        <div
          className="w-full h-full flex flex-col items-center justify-center gap-3 rounded-[11px] border border-dashed border-white/10"
          style={{ background: "var(--bg-nested)" }}
        >
          <Lock className="h-10 w-10" style={{ color: "#3a3d45" }} />
          <p className="text-[14px] text-center px-6" style={{ color: "#6b7080" }}>
            Generate all 5 Multi-View views to unlock Orbit
          </p>
          <p
            className="text-[12px] text-center px-6"
            style={{ color: "#4a4d55" }}
          >
            requires all 5 angles · you have {presentCount} of 5
          </p>
        </div>
      );
    }

    // Orbit tab: master/outside image (no overlay) + status line.
    const masterUrl =
      activeVersion?.imageUrl ||
      selectedMasterEntry?.imageUrl ||
      styleCard?.thumb_url ||
      null;
    const status = flowOrbitStatusLine(orbitStage.job);
    return (
      <div className="relative w-full h-full flex flex-col items-center justify-center gap-3 px-4">
        {masterUrl ? (
          <SignedOutputImage
            storageRef={masterUrl}
            alt="Master"
            className="max-w-full max-h-[calc(100%-2.5rem)] w-full h-full object-contain"
            hubThumb
            hubThumbLoaderSize={40}
            hubThumbWhitePlate
          />
        ) : (
          <div
            className="flex flex-col items-center justify-center gap-2 rounded-[11px] border border-dashed border-white/10 w-full flex-1"
            style={{ background: "var(--bg-nested)" }}
          >
            <Orbit className="h-10 w-10" style={{ color: "#3a3d45" }} />
          </div>
        )}
        <p
          className="text-[13px] text-center shrink-0"
          style={{ color: "#9aa0ad" }}
          data-testid="overlay-orbit-status"
        >
          {orbitStage.loading ? "Loading…" : status}
        </p>
      </div>
    );
  };

  const handleMakeMaster = onMakeMaster
    ? async (jobId: string) => {
        if (makeMasterBusyJobIdRef.current) return;
        makeMasterBusyJobIdRef.current = jobId;
        setMakeMasterBusyJobId(jobId);
        try {
          await onMakeMaster(jobId);
        } finally {
          makeMasterBusyJobIdRef.current = null;
          setMakeMasterBusyJobId(null);
        }
      }
    : undefined;

  const handlePromoteColorway = onPromoteColorway
    ? async (colorwayJobId: string) => {
        if (makeMasterBusyJobIdRef.current) return null;
        makeMasterBusyJobIdRef.current = colorwayJobId;
        setMakeMasterBusyJobId(colorwayJobId);
        try {
          const newId = await onPromoteColorway(colorwayJobId);
          if (newId) {
            setSelectedMasterId(newId);
            setSelectedColourwayId(null);
          }
          return newId;
        } finally {
          makeMasterBusyJobIdRef.current = null;
          setMakeMasterBusyJobId(null);
        }
      }
    : undefined;

  const renderMasterStack = () => {
    if (versionsLoading) {
      return <MasterStackSkeleton />;
    }
    // Soft-deleted versions are omitted from the live rail (Recently deleted section).
    const listForRail = sortedVersions.filter((v) => v.isHidden !== true);
    if (listForRail.length === 0) {
      return null;
    }
    return (
      <MasterVersionList
        versions={listForRail}
        selectedId={selectedMasterEntry?.id ?? null}
        selectedColourwayId={selectedColourwayId}
        activeMasterJobId={activeMasterJobId}
        pinnedJobIds={pinnedJobIds}
        colourwaysByParent={colourwaysByParent}
        totalVersionCount={effectiveTotalVersionCount}
        makeMasterBusyJobId={makeMasterBusyJobId}
        onSelect={selectVersionCard}
        onSelectColourway={selectColourway}
        onTogglePin={onTogglePin}
        onMakeMaster={handleMakeMaster}
        onRequestDelete={onRequestDeleteJob}
        onPromoteColorway={handlePromoteColorway}
        recentlyDeletedForStyle={recentlyDeletedForStyle}
        onRestoreDeleted={onRestoreDeleted}
        onPurgeForeverDeleted={onPurgeForeverDeleted}
      />
    );
  };

  const renderAngleColumn = () => {
    const listShellClass =
      "flex flex-col items-center w-full h-full min-h-0 overflow-y-auto stronom-scroll gap-3 py-1";

    if (anglesStage.loading) {
      return (
        <div className={listShellClass}>
          {Array.from({ length: ANGLE_STRIP_SKELETONS }).map((_, i) => (
            <div
              key={`sk-${i}`}
              className="shrink-0 rounded-lg animate-pulse"
              style={{
                width: VERSION_LIST_CARD_W,
                height: VERSION_LIST_CARD_IMG_H + 28,
                background: "rgba(255,255,255,0.08)",
              }}
            />
          ))}
        </div>
      );
    }

    return (
      <div className={listShellClass}>
        {angleSlots.map(({ kind, label, url }) => {
          const isSelected = kind === selectedAngleSlot?.kind;
          const selectable = !!url;
          if (selectable) {
            return (
              <AngleRailCard
                key={kind}
                kind={kind}
                label={label}
                url={url!}
                isSelected={isSelected}
                onSelect={() => setSelectedAngleKind(kind)}
              />
            );
          }
          return (
            <div
              key={kind}
              className="shrink-0 rounded-lg bg-transparent"
              style={{ width: VERSION_LIST_CARD_W }}
            >
              <div
                className="flex items-center justify-center rounded-lg border border-dashed border-white/10"
                style={{
                  height: VERSION_LIST_CARD_IMG_H,
                  background: "var(--bg-nested)",
                }}
                title={`Missing: ${label}`}
              >
                <Layers
                  className="h-4 w-4"
                  style={{ color: "var(--text-faint)" }}
                />
              </div>
              <p
                className="mt-1 truncate px-2 py-1 text-center text-[11px] font-medium"
                style={{ color: "#6b7080" }}
              >
                {label}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="flex flex-col h-full min-h-0"
      style={{ background: "var(--bg-surface)", fontFamily: "'Geist Sans', sans-serif" }}
    >
      <div className="shrink-0 grid grid-cols-[1fr_auto_1fr] items-center px-4 py-2 border-b border-white/[0.06] min-w-0">
        <div className="flex items-center gap-2 min-w-0 h-[34px]">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center rounded-md text-textMuted hover:text-white/75 transition-colors shrink-0"
            aria-label="Back"
            data-testid="overlay-flow-chain-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          <div className="min-w-0">
            {renaming && onRename ? (
              <input
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setRenaming(false);
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    const next = renameDraft.trim();
                    const prev =
                      styleCard?.name?.trim() ||
                      (localName != null ? localName.trim() : "");
                    if (!next || next === prev) {
                      setRenaming(false);
                      return;
                    }
                    void (async () => {
                      try {
                        await onRename(next);
                        setLocalName(next);
                        setRenaming(false);
                        setSavedFlash(true);
                        window.setTimeout(() => setSavedFlash(false), 1500);
                      } catch {
                        setRenaming(false);
                      }
                    })();
                  }
                }}
                onBlur={() => {
                  const next = renameDraft.trim();
                  const prev =
                    styleCard?.name?.trim() ||
                    (localName != null ? localName.trim() : "");
                  if (!next || next === prev) {
                    setRenaming(false);
                    return;
                  }
                  void (async () => {
                    try {
                      await onRename(next);
                      setLocalName(next);
                      setRenaming(false);
                      setSavedFlash(true);
                      window.setTimeout(() => setSavedFlash(false), 1500);
                    } catch {
                      setRenaming(false);
                    }
                  })();
                }}
                className="w-full max-w-[220px] bg-bgNested border border-white/[0.1] rounded-[6px] px-1.5 text-[15px] font-medium text-textPrimary outline-none"
              />
            ) : (
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="text-[15px] font-medium text-textPrimary truncate leading-tight">
                  {styleLabel}
                </p>
                {onRename ? (
                  <button
                    type="button"
                    className="shrink-0 text-textMuted hover:text-textPrimary"
                    aria-label="Rename style"
                    data-testid="overlay-rename-pencil"
                    onClick={() => {
                      setRenameDraft(
                        localName?.trim() ||
                          styleCard?.name?.trim() ||
                          "",
                      );
                      setRenaming(true);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                {styleActions ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label="Style actions"
                        data-testid="overlay-style-actions"
                        className="shrink-0 rounded-[5px] p-0.5 text-textMuted hover:bg-white/[0.07] hover:text-textPrimary"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <StyleActionsMenu
                      collections={styleActions.collections}
                      currentCollectionId={styleActions.currentCollectionId}
                      onMove={styleActions.onMove}
                      onNewCollection={styleActions.onNewCollection}
                      onRename={
                        onRename
                          ? () => {
                              setRenameDraft(
                                localName?.trim() ||
                                  styleCard?.name?.trim() ||
                                  "",
                              );
                              setRenaming(true);
                            }
                          : undefined
                      }
                      onDelete={styleActions.onDelete}
                      deleteLabel="Delete style"
                    />
                  </DropdownMenu>
                ) : null}
                {savedFlash ? (
                  <span className="text-[11px] text-[#34d399] shrink-0">
                    ✓ Saved
                  </span>
                ) : null}
              </div>
            )}
            {styleMeta ? (
              <p className="text-[11px] text-textMuted truncate leading-tight mt-0.5">
                {styleMeta}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center h-[34px]">
          {tabs.map((tab, idx) => {
            const Icon = tab.icon;
            const isActive = selectedTab === tab.id;
            const isDisabled =
              (tab.id === "orbit" || tab.id === "angles") && tab.locked;
            return (
              <button
                key={tab.id}
                type="button"
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) return;
                  setSelectedTab(tab.id);
                }}
                className={cn(
                  "relative flex items-center gap-2 h-full transition-colors",
                  idx === 0 ? "pl-0 pr-[15px]" : "px-[15px]",
                  isDisabled
                    ? "cursor-not-allowed text-[#3a3d45]"
                    : isActive
                      ? "text-white"
                      : "text-[#5a5f6b] hover:text-[#9aa0ad]",
                )}
              >
                <div className="relative flex items-center gap-2">
                  {isDisabled ? (
                    <Lock
                      className="h-3 w-3 shrink-0"
                      style={{ width: 12, height: 12 }}
                    />
                  ) : (
                    <Icon
                      className="shrink-0"
                      style={{
                        width: 15,
                        height: 15,
                        color: isActive ? "#5cc8f5" : "currentColor",
                      }}
                    />
                  )}
                  <span className="text-[13px] font-medium whitespace-nowrap leading-none">
                    {tab.label}
                  </span>
                  <span
                    className={cn(
                      "text-[10.5px] font-mono px-1.5 py-0.5 rounded-[5px] whitespace-nowrap leading-none",
                      isActive
                        ? "bg-[rgba(56,189,248,0.18)] text-[#5cc8f5]"
                        : "bg-white/[0.07] text-textSecondary",
                    )}
                  >
                    {tab.count}
                  </span>
                  {isActive && (
                    <span
                      className="absolute left-0 right-0 pointer-events-none"
                      style={{
                        top: "calc(100% + 3px)",
                        height: 2,
                        background: "#38bdf8",
                      }}
                      aria-hidden
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end h-[34px] gap-1 min-w-0">
          {loadingDesign ? (
            <Loader2
              className="h-4 w-4 animate-spin text-sky-400/80 shrink-0"
              aria-label="Loading design"
            />
          ) : designLoadFailed ? (
            <ImageOff
              className="h-4 w-4 text-textSecondary/80 shrink-0"
              aria-label="Failed to open design"
              data-testid="chain-design-load-failed"
            />
          ) : null}
          {showClose ? (
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full text-textSecondary hover:text-white hover:bg-white/[0.07] transition-colors shrink-0 ml-0.5"
              aria-label="Close"
              data-testid="overlay-flow-chain-close"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          "flex-1 min-h-0 flex items-stretch p-[14px_18px]",
          showRightColumn && "gap-[14px]",
        )}
      >
        <div
          className={cn(
            "h-full min-h-0 flex flex-col min-w-0",
            showRightColumn ? "shrink-0" : "flex-1 w-full",
          )}
          style={showRightColumn ? { width: PREVIEW_ZONE_W } : undefined}
        >
          <div className="flex-1 min-h-0 h-full bg-bgNested rounded-[11px] relative flex items-center justify-center overflow-hidden">
            {selectedTab === "master" && selectedColourwayEntry ? (
              <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1.5">
                <span
                  className="uppercase rounded-full"
                  style={{
                    background: "rgba(56,189,248,0.9)",
                    color: "#082f49",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "4px 9px",
                  }}
                  data-testid="overlay-hero-colorway-chip"
                >
                  Colorway ·{" "}
                  {selectedColourwayEntry.label ?? "Colorway"}
                </span>
              </div>
            ) : null}
            {selectedTab === "orbit" &&
            orbitStage.job?.id &&
            onRequestDeleteJob &&
            !orbitLocked ? (
              <div className="absolute right-3 top-3 z-20">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Orbit actions"
                      data-testid="overlay-orbit-actions"
                      className="rounded-[5px] p-0.5 text-textMuted hover:bg-white/[0.07] hover:text-textPrimary"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="min-w-[180px] rounded-[10px] border border-white/10 bg-bgSurface py-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
                  >
                    <DropdownMenuItem
                      className="cursor-pointer px-2.5 py-1.5 text-[12px] text-rose-300 focus:bg-rose-400/15 focus:text-rose-200"
                      data-testid="overlay-orbit-delete"
                      onSelect={() => {
                        const vLabel = activeVersion
                          ? `V${activeVersion.version}`
                          : "V?";
                        onRequestDeleteJob({
                          jobId: orbitStage.job!.id,
                          kind: "orbit",
                          label: vLabel,
                        });
                      }}
                    >
                      Delete Orbit
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : null}
            {renderHero()}
          </div>
        </div>
        {showMasterStack && (
          <div
            className={VERSION_COLUMN_CLASS}
            style={{ width: VERSION_COLUMN_W }}
          >
            {renderMasterStack()}
          </div>
        )}
        {showAngleColumn && (
          <div
            className={cn(VERSION_COLUMN_CLASS, "relative")}
            style={{ width: VERSION_COLUMN_W }}
            data-testid="overlay-angles-column"
          >
            {anglesStage.jobId && onRequestDeleteJob ? (
              <div className="absolute right-1 top-1 z-20">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Multi-View actions"
                      data-testid="overlay-angles-actions"
                      className="rounded-[5px] p-0.5 text-textMuted hover:bg-white/[0.07] hover:text-textPrimary"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="min-w-[180px] rounded-[10px] border border-white/10 bg-bgSurface py-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
                  >
                    <DropdownMenuItem
                      className="cursor-pointer px-2.5 py-1.5 text-[12px] text-rose-300 focus:bg-rose-400/15 focus:text-rose-200"
                      data-testid="overlay-angles-delete"
                      onSelect={() => {
                        const vLabel = fromVersion
                          ? `V${fromVersion.version}`
                          : activeVersion
                            ? `V${activeVersion.version}`
                            : "V?";
                        onRequestDeleteJob({
                          jobId: anglesStage.jobId!,
                          kind: "multi_view",
                          label: vLabel,
                        });
                      }}
                    >
                      Delete Multi-View
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : null}
            {renderAngleColumn()}
          </div>
        )}
      </div>

      <div
        className={cn(
          "shrink-0 border-t border-white/[0.06] p-[12px_18px]",
          showRightColumn
            ? "flex items-center gap-[14px]"
            : "flex flex-col items-center gap-2",
        )}
      >
        <div
          className={cn(
            "flex items-center justify-center min-w-0",
            showRightColumn ? "shrink-0" : "w-full",
          )}
          style={showRightColumn ? { width: PREVIEW_ZONE_W } : undefined}
        >
          <p
            className="text-[12px] truncate text-center max-w-full"
            style={{ color: "#6b7080" }}
          >
            {footerContext}
          </p>
        </div>
        {onOpenStage && (
          <div
            className={cn(
              "flex items-center justify-center min-w-0",
              showRightColumn ? "shrink-0" : "w-full",
            )}
            style={showRightColumn ? { width: VERSION_COLUMN_W } : undefined}
          >
            <OpenStageButton
              label={openLabel}
              icon={ArrowRight}
              onClick={handleOpen}
              disabled={openDisabled}
              loading={openingStage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
